import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Dropdown,
  Modal,
  DatePicker,
  Timeline,
  message,
  Drawer,
} from "antd";
import {
  FileTextOutlined,
  UploadOutlined,
  HistoryOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  CalendarOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  DatabaseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  equipmentReportPdfPost,
  getAllEquipmentReportsByEquipmentId,
  downloadEquipmentReportPdf,
  useEquipmentReports,
  viewEquipmentReportPdf,
  downloadEquipmentReportPdfByUrl,
  getReports,
  reportPdfbyId,
  equipmentReportPdfDelete,
} from "../../../apis";
import { EquipmentReportPdfI, ReportI } from "../../../interfaces";
import type { MenuProps } from "antd";
import dayjs, { Dayjs } from "dayjs";
import "../../css/reports-pdf.css";
import { PDFViewer } from "./view-pdf";
import moment from "moment";

import "moment/locale/es";

// Al inicio del componente:
moment.locale("es");
interface EquipmentReportsIntegrationProps {
  equipmentId: number;
  disabled?: boolean;
  showUploadReport?: boolean;
  showSmall?: boolean;
}

interface ReportsComponentProps {
  equipmentId: number;
  onUploadReport: (file: File, date: string) => Promise<boolean>;
  onGetReports: () => Promise<EquipmentReportPdfI[]>;
  onGetInternalReports: () => Promise<any[]>; // Nueva función para reportes internos
  onDownloadReport?: (report: EquipmentReportPdfI) => void;
  onViewReport?: (report: EquipmentReportPdfI) => void;
  onDownloadInternalReport?: (report: any) => void; // Nueva función para descargar internos
  onViewInternalReport?: (report: any) => void; // Nueva función para ver internos
  onDeleteReport?: (report: EquipmentReportPdfI) => void;
  disabled?: boolean;
  className?: string;
  showUploadReport?: boolean; // Nueva prop para controlar la visibilidad del botón de subir reportes
  showSmall?: boolean; // Nueva prop para controlar la visibilidad del botón de subir reportes
}

export const EquipmentReportsIntegration: React.FC<
  EquipmentReportsIntegrationProps
> = ({
  equipmentId,
  disabled = false,
  showUploadReport = true,
  showSmall = false,
}) => {
  // Usar el hook personalizado para obtener reportes
  const { data: reportsData, refetch } = useEquipmentReports(
    equipmentId,
    !!equipmentId
  );

  const handleUploadReport = async (
    file: File,
    date: string
  ): Promise<boolean> => {
    try {
      if (!equipmentId) {
        message.error("Debe guardar el equipo antes de subir reportes");
        return false;
      }

      const { data: result } = await equipmentReportPdfPost(
        file,
        equipmentId,
        date
      );

      if (result) {
        message.success(result.message);
        // Refrescar la lista de reportes
        refetch();
        return true;
      } else {
        message.error("Error al subir el reporte");
        return false;
      }
    } catch (error: any) {
      message.error(error.message || "Error al subir el reporte");
      console.error(error);
      return false;
    }
  };

  const handleGetReports = async (): Promise<EquipmentReportPdfI[]> => {
    try {
      if (!equipmentId) return [];

      const { data: dataResult } = await getAllEquipmentReportsByEquipmentId(
        equipmentId
      );

      if (dataResult) {
        // Transformar los datos del backend al formato esperado por el componente
        return dataResult.map((report: EquipmentReportPdfI): any => ({
          id: report.id,
          equipmentId: report.equipmentId,
          reportDate: report.reportDate,
          workDate: report.reportDate,
          fileUrl: report.src,
          fileName:
            report.fileName ||
            `Reporte ${report.reportDate ?? report.createdAt}.pdf`,
          createdAt: report.createdAt,
        }));
      }

      return [];
    } catch (error: any) {
      message.error("Error al cargar los reportes");
      console.error(error);
      return [];
    }
  };

  const handleGetInternalReports = async (): Promise<any[]> => {
    try {
      if (!equipmentId) return [];

      // Usar tu función existente pero filtrada por equipmentId
      const { data: dataResult } = await getReports({
        equipment: equipmentId,
        pagination: { pageIndex: 0, pageSize: 100 },
      });
      if (dataResult) {
        return dataResult.dato.map((report: any) => ({
          id: report.id,
          equipmentId: report.equipmentId || equipmentId,
          reportDate: report.reportDate,
          workDate: report.workDate,
          fileName: `Reporte Interno ${report.id}.pdf`,
          createdAt: report.createdAt,
          isInternal: true,
          // Otros campos que tenga tu reporte interno
          clientId: report.clientId,
          supplierId: report.supplierId,
          otId: report.otId,
        }));
      }

      return [];
    } catch (error: any) {
      message.error("Error al cargar los reportes internos");
      console.error(error);
      return [];
    }
  };

  const handleDownloadReport = async (report: EquipmentReportPdfI) => {
    try {
      if (!report.id) {
        message.error("ID de reporte no válido");
        return;
      }

      // Mostrar loading
      const loadingMessage = message.loading("Preparando descarga...", 0);

      try {
        // Usar la nueva función que maneja correctamente los blobs
        await downloadEquipmentReportPdf(report.id);
        loadingMessage();
        message.success("Descarga completada");
      } catch (error: any) {
        loadingMessage();
        // Si falla la descarga por ID, intentar descarga directa por URL (fallback)
        if (report.src) {
          try {
            const filename = `reporte-${dayjs(report.reportDate).format(
              "DD-MM-YYYY"
            )}.pdf`;
            await downloadEquipmentReportPdfByUrl(report.src, filename);
            message.success("Descarga completada");
          } catch (fallbackError) {
            throw error; // Lanzar el error original
          }
        } else {
          throw error;
        }
      }
    } catch (error: any) {
      message.error(error.message || "Error al descargar el reporte");
      console.error("Download error:", error);
    }
  };

  const handleViewReport = async (report: EquipmentReportPdfI) => {
    try {
      if (!report.id) {
        message.error("ID de reporte no válido");
        return;
      }

      // OPCIÓN 1: Abrir directamente en nueva pestaña (más simple)
      await viewEquipmentReportPdf(report.id);
      message.success("PDF abierto en nueva pestaña");

      // OPCIÓN 2: Si quieres usar el modal viewer, comenta lo de arriba y usa esto:
      // setSelectedReportForView(report);
      // setPdfViewerVisible(true);
    } catch (error: any) {
      message.error(error.message || "Error al abrir el reporte");
      console.error("View error:", error);
    }
  };

  const handleDownloadInternalReport = async (report: any) => {
    try {
      if (!report.id) {
        message.error("ID de reporte no válido");
        return;
      }

      const loadingMessage = message.loading("Descargando PDF...", 0);

      // Quitar ceros del ID
      const cleanId =
        typeof report.id === "string"
          ? parseInt(report.id.toString().replace(/^0+/, ""), 10)
          : report.id;

      const { data: dataPdf } = await reportPdfbyId(Number(cleanId));
      message.destroy();

      const downloadLink = document.createElement("a");
      downloadLink.href = `data:application/pdf;base64,${dataPdf}`;
      downloadLink.download = `Reporte-Interno-${cleanId}.pdf`;
      downloadLink.click();

      message.success("Descargado con éxito", 2);
    } catch (err: any) {
      console.log(err);
      message.destroy();
      message.error(err.toString());
    }
  };

  const handleViewInternalReport = async (report: any) => {
    try {
      if (!report.id) {
        message.error("ID de reporte no válido");
        return;
      }

      const loadingMessage = message.loading("Generando PDF...", 0);

      // Quitar ceros del ID
      const cleanId =
        typeof report.id === "string"
          ? parseInt(report.id.toString().replace(/^0+/, ""), 10)
          : report.id;

      const { data: dataPdf } = await reportPdfbyId(Number(cleanId));
      message.destroy();

      // Abrir en nueva pestaña en lugar de descargar
      const newWindow = window.open();
      newWindow!.document.write(
        `<iframe src="data:application/pdf;base64,${dataPdf}" width="100%" height="100%"></iframe>`
      );

      message.success("PDF abierto en nueva pestaña", 2);
    } catch (err: any) {
      console.log(err);
      message.destroy();
      message.error(err.toString());
    }
  };

  const handleDeleteReport = async (report: EquipmentReportPdfI) => {
    try {
      if (!report.id) {
        message.error("Debe guardar el equipo antes de subir reportes");
        return;
      }

      const { data: dataResult } = await equipmentReportPdfDelete(report.id);
      message.success(dataResult);
      return "ok";
    } catch (err: any) {
      console.log(err);
      message.error(err.toString());
    }
  };

  return (
    <ReportsComponent
      equipmentId={equipmentId}
      onUploadReport={handleUploadReport}
      onGetReports={handleGetReports}
      onGetInternalReports={handleGetInternalReports}
      onDownloadReport={handleDownloadReport}
      onViewReport={handleViewReport}
      onDownloadInternalReport={handleDownloadInternalReport}
      onViewInternalReport={handleViewInternalReport}
      onDeleteReport={handleDeleteReport}
      disabled={!equipmentId || disabled}
      showUploadReport={showUploadReport}
      className="equipment-reports"
      showSmall={showSmall}
    />
  );
};

const isPWA = (): boolean => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone ||
    document.referrer.includes("android-app://")
  );
};

export const ReportsComponent: React.FC<ReportsComponentProps> = ({
  equipmentId,
  onViewReport,
  onUploadReport,
  onGetReports,
  onGetInternalReports,
  onDownloadReport,
  onDownloadInternalReport,
  onViewInternalReport,
  onDeleteReport,
  disabled = false,
  className = "",
  showUploadReport = true,
  showSmall = false,
}) => {
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
  const [selectedReportForView, setSelectedReportForView] =
    useState<EquipmentReportPdfI | null>(null);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [reportDate, setReportDate] = useState<Dayjs | null>(dayjs());
  const [reports, setReports] = useState<any[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [activeReportType, setActiveReportType] = useState<
    "external" | "internal"
  >("external");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const menuItems: MenuProps["items"] = [
    {
      key: "external-history",
      label: (
        <div className="dropdown-menu-item">
          <FieldTimeOutlined className="dropdown-icon" />
          <span>Reportes Externos</span>
        </div>
      ),
      onClick: () => handleShowHistory("external"),
    },
    {
      key: "internal-history",
      label: (
        <div className="dropdown-menu-item">
          <DatabaseOutlined className="dropdown-icon" />
          <span>Reportes Internos</span>
        </div>
      ),
      onClick: () => handleShowHistory("internal"),
    },
  ];

  const menuItemsComplete: MenuProps["items"] = [
    {
      key: "upload",
      label: (
        <div className="dropdown-menu-item">
          <UploadOutlined className="dropdown-icon" />
          <span>Subir Reporte Externo</span>
        </div>
      ),
      onClick: () => setUploadModalVisible(true),
    },
    {
      key: "external-history",
      label: (
        <div className="dropdown-menu-item">
          <FieldTimeOutlined className="dropdown-icon" />
          <span>Reportes Externos</span>
        </div>
      ),
      onClick: () => handleShowHistory("external"),
    },
    {
      key: "internal-history",
      label: (
        <div className="dropdown-menu-item">
          <DatabaseOutlined className="dropdown-icon" />
          <span>Reportes Internos</span>
        </div>
      ),
      onClick: () => handleShowHistory("internal"),
    },
  ];

  async function handleShowHistory(reportType: "external" | "internal") {
    setActiveReportType(reportType);
    setHistoryModalVisible(true);
    setHistoryLoading(true);

    try {
      let data;
      if (reportType === "external") {
        data = await onGetReports();
      } else {
        data = await onGetInternalReports();
      }
      setReports(data);
    } catch (error) {
      message.error(
        `Error al cargar los reportes ${
          reportType === "external" ? "externos" : "internos"
        }`
      );
      console.error(error);
    } finally {
      setHistoryLoading(false);
    }
  }

  // Función para manejar la selección de archivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar que es un PDF
    if (file.type !== "application/pdf") {
      message.error("Solo se permiten archivos PDF");
      return;
    }

    // Validar tamaño (10MB máximo)
    if (file.size > 10 * 1024 * 1024) {
      message.error("El archivo debe ser menor a 10MB");
      return;
    }

    setSelectedFile(file);
  };

  // Función para limpiar la selección de archivo
  const clearFileSelection = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      message.error("Por favor selecciona un archivo PDF");
      return;
    }

    if (!reportDate) {
      message.error("Por favor selecciona una fecha para el reporte");
      return;
    }

    setLoading(true);
    try {
      console.log("into-upload");
      const success = await onUploadReport(
        selectedFile,
        reportDate.format("YYYY-MM-DD")
      );

      if (success) {
        setUploadModalVisible(false);
        clearFileSelection();
        setReportDate(dayjs());
      }
    } catch (error) {
      message.error("Error al subir el reporte");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (
    report: any,
    action: "download" | "view" = "download"
  ) => {
    try {
      if (action === "download") {
        if (report.isInternal) {
          // Usar la función para reportes internos
          await onDownloadInternalReport?.(report);
        } else {
          // Usar la función para reportes externos (lógica existente)
          if (!report.id) {
            message.error("ID de reporte no válido");
            return;
          }

          const loadingMessage = message.loading("Preparando descarga...", 0);

          try {
            await downloadEquipmentReportPdf(report.id);
            loadingMessage();
            message.success("Descarga completada");
          } catch (error: any) {
            loadingMessage();
            if (report.src) {
              try {
                const filename = `reporte-externo-${dayjs(
                  report.reportDate
                ).format("DD-MM-YYYY")}.pdf`;
                await downloadEquipmentReportPdfByUrl(report.src, filename);
                message.success("Descarga completada");
              } catch (fallbackError) {
                throw error;
              }
            } else {
              throw error;
            }
          }
        }
      } else if (action === "view") {
        // En móvil siempre descargar
        if (isMobile) {
          message.info("Descargando PDF para visualización...");
          await handleDownload(report, "download");
          return;
        }

        // En desktop PWA: abrir en navegador externo
        if (isPWA()) {
          if (report.isInternal) {
            await onViewInternalReport?.(report);
          } else {
            try {
              if (report.src) {
                window.open(report.src, "_blank", "noopener,noreferrer");
                message.success("PDF abierto en navegador");
              } else {
                await viewEquipmentReportPdf(report.id);
                message.success("PDF abierto en navegador");
              }
            } catch (error) {
              message.info("Descargando PDF...");
              await handleDownload(report, "download");
            }
          }
        } else {
          // Desktop navegador normal
          if (report.isInternal) {
            await onViewInternalReport?.(report);
          } else {
            setSelectedReportForView(report);
            setPdfViewerVisible(true);
          }
        }
      }
    } catch (error: any) {
      console.error("Error handling PDF action:", error);
      message.error(error.message || "Error al procesar el archivo");
    }
  };

  // Función para cerrar el visualizador
  const handleClosePdfViewer = () => {
    setPdfViewerVisible(false);
    setSelectedReportForView(null);
  };

  // Función para descargar desde el visualizador
  const handleDownloadFromViewer = async (report: EquipmentReportPdfI) => {
    if (onDownloadReport) {
      await onDownloadReport(report);
    }
  };

  const handleDelete = async (report: EquipmentReportPdfI) => {
    if (onDeleteReport) {
      await onDeleteReport(report);
      handleShowHistory("external");
    }
  };

  const timelineItems = reports.map((report) => ({
    dot: <FilePdfOutlined className="timeline-pdf-icon" />,
    children: (
      <div className="timeline-item">
        <div className="timeline-header">
          <div className="timeline-date">
            <CalendarOutlined className="date-icon" />
            {/* Usar workDate para internos, reportDate para externos */}
            {report.workDate
              ? (() => {
                  const fecha = moment(report.workDate, "YYYY-MM-DD", true); // strict parsing
                  return fecha.isValid()
                    ? fecha.format("D [de] MMMM [del] YYYY")
                    : report.workDate; // fallback al valor original
                })()
              : (() => {
                  const fecha = moment(report.reportDate, "YYYY-MM-DD", true); // strict parsing
                  return fecha.isValid()
                    ? fecha.format("D [de] MMMM [del] YYYY")
                    : report.reportDate; // fallback al valor original
                })()}
          </div>
          <div className="timeline-actions">
            <Button
              type="text"
              size="small"
              icon={isMobile ? <DownloadOutlined /> : <EyeOutlined />}
              onClick={() => handleDownload(report, "view")}
              className="timeline-view-btn"
              title={
                isMobile ? "Descargar PDF" : isPWA() ? "Abrir PDF" : "Ver PDF"
              }
            >
              {!isMobile &&
                (isMobile ? "Descargar" : isPWA() ? "Abrir" : "Ver")}
            </Button>

            {!isMobile && (
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(report, "download")}
                className="timeline-download-btn"
                title="Descargar PDF"
              >
                Descargar
              </Button>
            )}
            {!report.isInternal && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(report)}
                className="timeline-delete-btn"
                title="Eliminar"
              >
                {!isMobile && "Eliminar"}
              </Button>
            )}
          </div>
        </div>
        <div className="timeline-content">
          <div className="timeline-filename">
            <FilePdfOutlined className="file-icon" />
            <span>{report.fileName}</span>
          </div>
          {/* Mostrar información adicional para reportes internos */}
          {report.workDate && (
            <div
              className="timeline-work-info"
              style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
            >
              {report.workPerson && <div>Técnico: {report.workPerson}</div>}
              {report.client && <div>Cliente: {report.client}</div>}
              {report.equipment && <div>Equipo: {report.equipment}</div>}
            </div>
          )}
        </div>

        {report.createdAt && (
          <div className="timeline-created">
            {report.workDate ? "Generado" : "Subido"}: {report.createdAt}
          </div>
        )}
      </div>
    ),
  }));

  const getHistoryTitle = () => {
    return activeReportType === "external"
      ? "Reportes Externos"
      : "Reportes Internos";
  };

  return (
    <>
      <div className={`reports-component ${className}`}>
        <Dropdown
          menu={{ items: showUploadReport ? menuItemsComplete : menuItems }}
          disabled={disabled}
          placement="bottomLeft"
          trigger={["click"]}
          overlayClassName="reports-dropdown-overlay"
        >
          <Button
            type={showUploadReport ? "primary" : "dashed"}
            icon={<FileTextOutlined />}
            disabled={disabled}
            size={"middle"}
          >
            {(showSmall || !isMobile) && "Reportes"}
          </Button>
        </Dropdown>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        {/* Modal/Drawer para subir reporte externo */}
        {isMobile ? (
          <Drawer
            title={
              <div className="drawer-title">
                <CloudUploadOutlined className="drawer-icon" />
                Subir Reporte Externo
              </div>
            }
            closeIcon={<CloseOutlined style={{ display: "none" }} />}
            placement="bottom"
            open={uploadModalVisible}
            onClose={() => {
              setUploadModalVisible(false);
              clearFileSelection();
              setReportDate(dayjs());
            }}
            height="auto"
            className="upload-drawer"
            extra={
              <Button
                type="primary"
                danger
                icon={<CloseOutlined />}
                onClick={() => {
                  setUploadModalVisible(false);
                  clearFileSelection();
                  setReportDate(dayjs());
                }}
              />
            }
          >
            <div className="upload-drawer-content">
              <div className="upload-field">
                <div className="upload-label-container">
                  <FilePdfOutlined className="label-icon" />
                  <label className="upload-label">
                    Seleccionar archivo PDF
                  </label>
                </div>
                <div
                  className="custom-upload-area"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="upload-area">
                    <CloudUploadOutlined className="upload-icon" />
                    <div className="upload-text">
                      <div className="upload-hint">
                        {selectedFile
                          ? selectedFile.name
                          : "Toca para seleccionar"}
                      </div>
                      <div className="upload-subtext">PDF • Máx. 10MB</div>
                    </div>
                  </div>
                </div>
                {selectedFile && (
                  <div className="selected-file">
                    <FilePdfOutlined
                      style={{ color: "#ff4d4f", marginRight: 8 }}
                    />
                    <span>{selectedFile.name}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={clearFileSelection}
                      style={{ marginLeft: 8 }}
                    />
                  </div>
                )}
              </div>

              <div className="upload-field">
                <div className="upload-label-container">
                  <CalendarOutlined className="label-icon" />
                  <label className="upload-label">Fecha del reporte</label>
                </div>
                <DatePicker
                  value={reportDate}
                  onChange={setReportDate}
                  format="DD/MM/YYYY"
                  placeholder="Seleccionar fecha"
                  style={{ width: "100%", height: "48px" }}
                  allowClear={false}
                  size="large"
                />
              </div>

              <div className="drawer-actions">
                <Button
                  block
                  size="large"
                  onClick={() => {
                    setUploadModalVisible(false);
                    clearFileSelection();
                    setReportDate(dayjs());
                  }}
                  className="cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="primary"
                  block
                  size="large"
                  loading={loading}
                  onClick={handleUpload}
                  icon={<UploadOutlined />}
                  className="upload-btn"
                  disabled={!selectedFile}
                >
                  Subir Reporte
                </Button>
              </div>
            </div>
          </Drawer>
        ) : (
          <Modal
            title={
              <div className="modal-title">
                <CloudUploadOutlined className="modal-icon" />
                Subir Reporte Externo
              </div>
            }
            centered
            open={uploadModalVisible}
            onOk={handleUpload}
            cancelButtonProps={{
              danger: true,
            }}
            onCancel={() => {
              setUploadModalVisible(false);
              clearFileSelection();
              setReportDate(dayjs());
            }}
            closeIcon={<CloseOutlined style={{ display: "none" }} />}
            confirmLoading={loading}
            okText="Subir Reporte"
            cancelText="Cancelar"
            className="upload-modal"
            okButtonProps={{
              icon: <UploadOutlined />,
              size: "middle",
              disabled: !selectedFile,
            }}
          >
            <div className="upload-modal-content">
              <div className="upload-field">
                <div className="upload-label-container">
                  <FilePdfOutlined className="label-icon" />
                  <label className="upload-label">Archivo PDF</label>
                </div>
                <div
                  className="custom-upload-area"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="upload-area">
                    <CloudUploadOutlined className="upload-icon" />
                    <div className="upload-text">
                      <div className="upload-hint">
                        {selectedFile
                          ? selectedFile.name
                          : "Arrastra o haz clic para seleccionar"}
                      </div>
                      <div className="upload-subtext">
                        Solo archivos PDF • Máximo 10MB
                      </div>
                    </div>
                  </div>
                </div>
                {selectedFile && (
                  <div className="selected-file">
                    <FilePdfOutlined
                      style={{ color: "#ff4d4f", marginRight: 8 }}
                    />
                    <span>{selectedFile.name}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={clearFileSelection}
                      style={{ marginLeft: 8 }}
                    />
                  </div>
                )}
              </div>

              <div className="upload-field">
                <div className="upload-label-container">
                  <CalendarOutlined className="label-icon" />
                  <label className="upload-label">Fecha del reporte</label>
                </div>
                <DatePicker
                  value={reportDate}
                  onChange={setReportDate}
                  format="DD/MM/YYYY"
                  placeholder="Seleccionar fecha"
                  style={{ width: "100%" }}
                  allowClear={false}
                />
              </div>
            </div>
          </Modal>
        )}

        {/* Modal/Drawer para histórico */}
        {isMobile ? (
          <Drawer
            title={
              <div className="drawer-title">
                <HistoryOutlined className="drawer-icon" />
                &nbsp; {getHistoryTitle()}
              </div>
            }
            closeIcon={<CloseOutlined style={{ display: "none" }} />}
            placement="bottom"
            open={historyModalVisible}
            onClose={() => setHistoryModalVisible(false)}
            height="80%"
            className="history-drawer"
            extra={
              <Button
                type="primary"
                icon={<CloseOutlined />}
                danger
                onClick={() => setHistoryModalVisible(false)}
              />
            }
          >
            <div className="history-drawer-content">
              {historyLoading ? (
                <div className="history-loading">
                  <div className="loading-spinner"></div>
                  <p>Cargando reportes...</p>
                </div>
              ) : reports.length > 0 ? (
                <Timeline
                  items={timelineItems}
                  className="reports-timeline mobile-timeline"
                />
              ) : (
                <div className="no-reports">
                  <FilePdfOutlined className="no-reports-icon" />
                  <h3>No hay reportes</h3>
                  <p>
                    Aún no se han{" "}
                    {activeReportType === "external" ? "subido" : "generado"}{" "}
                    reportes para este equipo
                  </p>
                </div>
              )}
            </div>
          </Drawer>
        ) : (
          <Modal
            title={
              <div className="modal-title">
                <HistoryOutlined className="modal-icon" />
                &nbsp;{getHistoryTitle()}
              </div>
            }
            open={historyModalVisible}
            onCancel={() => setHistoryModalVisible(false)}
            footer={
              <Button
                type="primary"
                danger
                onClick={() => setHistoryModalVisible(false)}
              >
                Cerrar
              </Button>
            }
            width={700}
            centered
            closeIcon={<CloseOutlined style={{ display: "none" }} />}
            className="history-modal"
          >
            <div className="history-modal-content">
              {historyLoading ? (
                <div className="history-loading">
                  <div className="loading-spinner"></div>
                  <p>Cargando reportes...</p>
                </div>
              ) : reports.length > 0 ? (
                <Timeline items={timelineItems} className="reports-timeline" />
              ) : (
                <div className="no-reports">
                  <FilePdfOutlined className="no-reports-icon" />
                  <h3>No hay reportes disponibles</h3>
                  <p>
                    Aún no se han{" "}
                    {activeReportType === "external" ? "subido" : "generado"}{" "}
                    reportes para este equipo
                  </p>
                </div>
              )}
            </div>
          </Modal>
        )}
      </div>

      {/* Visor de PDF */}
      <PDFViewer
        report={selectedReportForView}
        visible={pdfViewerVisible}
        onClose={handleClosePdfViewer}
        isMobile={isMobile}
        onDownload={handleDownloadFromViewer}
      />
    </>
  );
};
